import { Body, Caption, VIconActionButton } from '@vrzl/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CRITERIA_SEARCH_EMPTY_TITLE, CriteriaSearch, filterByQuery } from '@/domains/criteria';

export type CriterionOption = { code: string; name: string };

type CriterionPickerProps = {
  options: readonly CriterionOption[];
  /** Codes die al in het blok staan; die zijn niet te kiezen (geen dubbele criteria). */
  exclude: readonly string[];
  /** Labels van de blokken waarin een code al staat ("Staat ook in: …"). */
  usedIn: (code: string) => string[];
  onAdd: (code: string) => void;
};

/** Zoeken terwijl je typt, met een inline keuzelijst: geen popover, dus geen focusproblemen. */
export function CriterionPicker({ options, exclude, usedIn, onAdd }: CriterionPickerProps) {
  const [query, setQuery] = useState('');

  const candidates = useMemo(() => {
    const available = options
      .filter((option) => !exclude.includes(option.code))
      .sort((x, y) => x.name.localeCompare(y.name, 'nl'));
    return filterByQuery(available, query, {
      getName: (option) => option.name,
      getValues: (option) => [option.code],
    });
  }, [options, exclude, query]);

  return (
    <div className="flex flex-col gap-2">
      <Body as="span" weight="medium">
        Criterium toevoegen
      </Body>
      <CriteriaSearch value={query} onChange={setQuery} placeholder="Zoek op naam of code" />
      <ul className="max-h-72 overflow-y-auto rounded-lg border border-border">
        {candidates.length === 0 ? (
          <li className="px-3 py-4">
            <Caption>{CRITERIA_SEARCH_EMPTY_TITLE}</Caption>
          </li>
        ) : (
          candidates.map((option) => {
            const others = usedIn(option.code);
            return (
              <li
                key={option.code}
                className="flex items-center gap-2 border-t border-border px-3 py-2 first:border-t-0"
              >
                <div className="min-w-0 flex-1">
                  <Body as="span" className="block">
                    {option.name}
                  </Body>
                  <Caption className="block break-all font-mono">{option.code}</Caption>
                  {others.length > 0 && (
                    <Caption tone="subtle" className="block">
                      Staat ook in: {others.join(', ')}
                    </Caption>
                  )}
                </div>
                <VIconActionButton
                  icon={Plus}
                  label={`Voeg ${option.name} toe`}
                  className="text-neutral-700 hover:bg-neutral-200/50 hover:text-neutral-900"
                  onClick={() => onAdd(option.code)}
                />
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
